/**
 * 애플리케이션 메인 컴포넌트
 *
 * 주요 기능:
 * - 전역 인증 상태 관리 및 토큰 유효성 검사
 * - 오디오 플레이어 초기화 및 생명주기 관리
 * - 외부 SDK 초기화 (Kakao)
 * - 탭 간 인증 상태 동기화 (LocalStorage 이벤트)
 * - 전역 스낵바 및 테마 제공
 *
 * 초기화 순서:
 * 1. 주기적 토큰 유효성 검사 (30초마다)
 * 2. 윈도우 포커스 시 토큰 유효성 검사
 * 3. 오디오 플레이어 초기화
 * 4. Kakao SDK 초기화
 * 5. 초기 인증 상태 로드 (액세스/리프레시 토큰 기반)
 * 6. 탭 간 스토리지 동기화 리스너 등록
 *
 * 사용처: main.tsx에서 루트 렌더링
 */

import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSyncPlayHistoryWithServer } from "@/hooks/useSyncPlayHistoryWithServer";
import { useAuthStore } from "@/stores/authStore";
import { useSnackbarStore } from "@/stores/snackbarStore";
import {
  cleanupAudioPlayer,
  initializeAudioPlayer,
} from "@/stores/audioPlayerStore";
import { Snackbar } from "@/components/common/Snackbar";
import { logger } from "@/utils/logger";
import {
  performPeriodicTokenCheck,
  handleTokenValidationOnFocus,
} from "@/utils/authValidation";
import { tokenService } from "@/utils/tokenService";
import { initKakao } from "@/utils/kakaoAuth";
import "./App.css";
import { router } from "./router";
import { GlobalStyle } from "./styles/GlobalStyle";
import { ThemeProvider } from "./styles/ThemeContext";

// 토큰 유효성 검사 주기 설정
const TOKEN_CHECK_INTERVAL = 30000; // 30초 - 사용자 세션 유지 및 보안을 위한 주기적 검증

function App() {
  const {
    isOpen,
    message,
    showCheckIcon,
    showCloseIcon,
    duration,
    hideSnackbar,
  } = useSnackbarStore();
  const { clearUser, setUser, user } = useAuthStore();
  const { getUserProfile } = useAuth();

  // 재생 기록을 서버 데이터(장바구니 및 구매 내역)와 동기화
  useSyncPlayHistoryWithServer();

  // ========================================
  // 1. 주기적 토큰 유효성 검사 (30초마다)
  // ========================================
  useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      performPeriodicTokenCheck();
    }, TOKEN_CHECK_INTERVAL);

    return () => clearInterval(tokenCheckInterval);
  }, []);

  // ========================================
  // 2. 윈도우 포커스 시 토큰 유효성 검사
  // ========================================
  useEffect(() => {
    window.addEventListener("focus", handleTokenValidationOnFocus);

    return () => {
      window.removeEventListener("focus", handleTokenValidationOnFocus);
    };
  }, []);

  // ========================================
  // 3. 오디오 플레이어 초기화 (1회 실행)
  // ========================================
  useEffect(() => {
    initializeAudioPlayer();

    return () => {
      cleanupAudioPlayer();
    };
  }, []);

  // ========================================
  // 4. Kakao SDK 초기화 (1회 실행)
  // ========================================
  useEffect(() => {
    initKakao();
  }, []);

  // ========================================
  // 5. 초기 인증 검사 및 사용자 프로필 로드 (1회 실행)
  // ========================================
  useEffect(() => {
    /**
     * 앱 인증 상태 초기화
     * 유효한 토큰이 있는지 확인하고, 인증된 경우 사용자 프로필을 가져옴
     */
    const initializeApp = async () => {
      const accessToken = tokenService.getAccessToken();
      const refreshToken = tokenService.getRefreshToken();

      if (accessToken) {
        // 액세스 토큰이 있는 경우 - 사용자 프로필 가져오기
        try {
          const userProfile = await getUserProfile();

          if (userProfile) {
            setUser(userProfile);
            // 장바구니는 TanStack Query에 의해 자동으로 가져옴
          } else {
            // 유효하지 않은 토큰
            tokenService.removeTokens();
            clearUser();
          }
        } catch (error) {
          logger.error("사용자 초기화 실패:", { error });
          tokenService.removeTokens();
          clearUser();
        }
      } else if (!accessToken && refreshToken) {
        // 리프레시 토큰만 있고 액세스 토큰이 없는 경우 - 토큰 갱신 시도
        // axios 인터셉터가 토큰 갱신을 자동으로 처리함
        try {
          const userProfile = await getUserProfile();

          if (userProfile) {
            setUser(userProfile);
            // 장바구니는 TanStack Query에 의해 자동으로 가져옴
          } else {
            tokenService.removeTokens();
            window.location.href = "/";
          }
        } catch (error) {
          logger.error("리프레시 토큰으로 사용자 초기화 실패:", { error });
          tokenService.removeTokens();
          window.location.href = "/";
        }
      } else {
        // 토큰이 없는 경우 - 사용자 상태 초기화
        clearUser();
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 마운트 시 1회만 실행

  // ========================================
  // 6. 탭 간 스토리지 동기화 (반응형)
  // ========================================
  useEffect(() => {
    /**
     * 다른 탭의 스토리지 변경 사항 처리
     * 브라우저 탭 간 인증 상태 동기화
     */
    const handleStorageChange = async (e: StorageEvent) => {
      // 관련 없는 스토리지 키는 조기 반환
      if (e.key !== "accessToken" && e.key !== null) {
        return;
      }

      // 토큰 변경 처리 (다른 탭에서 로그인/로그아웃)
      if (e.key === null || e.key === "accessToken") {
        const newToken = tokenService.getAccessToken();

        if (!newToken && user) {
          // 다른 탭에서 로그아웃됨
          clearUser();
          window.location.href = "/";
        } else if (newToken && !user) {
          // 다른 탭에서 로그인됨 - 사용자 상태 초기화
          try {
            const userProfile = await getUserProfile();
            if (userProfile) {
              setUser(userProfile);
              // 장바구니는 TanStack Query에 의해 자동으로 가져옴
            }
          } catch (error) {
            logger.error("다른 탭의 로그인 동기화 실패:", { error });
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [user, getUserProfile, setUser, clearUser]);

  return (
    <ThemeProvider>
      <GlobalStyle />
      <RouterProvider router={router} />
      <Snackbar
        isOpen={isOpen}
        message={message}
        showCheckIcon={showCheckIcon}
        showCloseIcon={showCloseIcon}
        duration={duration}
        onClose={hideSnackbar}
      />
    </ThemeProvider>
  );
}

export default App;
